import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import useDebounce from '../../../components/Shared/Delay/index.tsx';
import ConfirmBox from '../../../components/FlowBuilder/Common/ConfirmBox/index.tsx';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { EllipsisWithTooltip } from '../../../components/Shared/Tables/TableV1.tsx';
import { dateFormate } from '../../../utils/helper.jsx';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider.jsx';
import { TablePagination } from '../../../components/ui';
import useTemplateList from '../hooks/useTemplateList';
import useTemplateListCounts from '../hooks/useTemplateListCounts';
import useTemplatePermissions from '../hooks/useTemplatePermissions';
import useTemplateSendDialog from '../hooks/useTemplateSendDialog';
import useTemplateSx from '../hooks/useTemplateSx';
import TemplatePageHeader from '../components/list/TemplatePageHeader';
import TemplateStatusTabs from '../components/list/TemplateStatusTabs';
import OptinUsersPanel from '../components/list/OptinUsersPanel';
import TemplateFiltersBar from '../components/list/TemplateFiltersBar';
import TemplateColumnHeader from '../components/list/TemplateColumnHeader';
import TemplateNameCell from '../components/list/TemplateNameCell';
import TemplateRowActions from '../components/list/TemplateRowActions';
import TemplateStatusCell from '../components/list/TemplateStatusCell';
import TemplateHealthCell from '../components/list/TemplateHealthCell';
import useBatchTemplateHealth, { getTemplateRowKey } from '../hooks/useBatchTemplateHealth';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import TemplateDataTable from '../components/shared/TemplateDataTable';
import StatusChip from '../components/ui/StatusChip';
import TemplateSendDialog from '../components/send/TemplateSendDialog';
import HealthPanel from '../components/health/HealthPanel';
import HealthLiveBar from '../components/health/HealthLiveBar';
import { TEMPLATE_BASE_PATH } from '../constants/templateApi';
import { CategoryOptions, LanguageList } from '../utils/templateConstants';
import { getRejectionReason, isRejectedTemplate } from '../utils/getRejectionReason';
import { templateService } from '../services/templateService';
import { isTemplateDeletable, getArchiveDeleteLabel } from '../utils/templateArchiveUtils';
import { getOrganisationNameFromTemplate } from '../utils/normalizeTemplate';

function getLanguageLabel(code) {
  return LanguageList.find((item) => item.value === code)?.label ?? code ?? '—';
}

function getCategoryLabel(value) {
  return CategoryOptions.find((item) => item.value === value)?.label ?? value ?? '—';
}

export default function TemplateListPage() {
  return (
    <FeatureThemeProvider>
      <TemplateListPageContent />
    </FeatureThemeProvider>
  );
}

function TemplateListPageContent() {
  const styles = useTemplateSx();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSnackBar } = useSnackBar();
  const { openSendDialog } = useTemplateSendDialog();
  const { canCreate, canEdit, canViewPerformance, canViewOptin } = useTemplatePermissions();
  const {
    filters,
    updateFilters,
    resetFilters,
    templates,
    count,
    error,
    isLoading,
    refreshList,
  } = useTemplateList();

  const loggedInUser = useRecoilValue(userInformationAtom);
  const loggedInOrganisation = loggedInUser?.profile?.organisation ?? null;
  const loggedInOrganisationId = loggedInOrganisation?.organisation_id ?? '';
  const activeCompanyId = filters.organisation || loggedInOrganisationId || '';

  const {
    healthMap,
    isCheckingAll: isCheckingPageHealth,
    progress: pageHealthProgress,
    checkSingle: checkSingleTemplateHealth,
    checkBatch: checkBatchTemplatesHealth,
  } = useBatchTemplateHealth({ companyId: activeCompanyId });

  const isOptinView = canViewOptin && searchParams.get('view') === 'optin';
  const isHealthView = searchParams.get('view') === 'health';
  const healthTab = Number(searchParams.get('health_tab') || 0);
  const healthWabaNumber = searchParams.get('waba_number') || '';
  const healthTemplateName = searchParams.get('template_name') || '';

  const { counts: tabCounts, isLoading: countsLoading } = useTemplateListCounts();

  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  const [sorting, setSorting] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [syncingRowKey, setSyncingRowKey] = useState(null);
  const [optinCount, setOptinCount] = useState(undefined);
  const debouncedSearch = useDebounce(searchInput, 500);

  const openHealthView = (tab = 0, wabaNumber = '', templateName = '') => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', 'health');
      if (tab !== undefined) next.set('health_tab', String(tab));
      if (wabaNumber) next.set('waba_number', String(wabaNumber));
      if (templateName) next.set('template_name', String(templateName));
      return next;
    }, { replace: true });
  };

  const closeHealthView = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('view');
      next.delete('health_tab');
      next.delete('waba_number');
      next.delete('template_name');
      return next;
    }, { replace: true });
  };

  const statusTabCounts = useMemo(
    () => ({
      ...tabCounts,
      optin: optinCount,
    }),
    [tabCounts, optinCount]
  );

  const selectedTemplates = useMemo(
    () => templates.filter((row) => rowSelection[`${row.name}::${row.bot_number}`]),
    [templates, rowSelection]
  );

  const handleBulkArchiveRestore = async (confirmed) => {
    const action = bulkAction;
    setBulkAction(null);
    if (!confirmed || !action || !selectedTemplates.length) return;

    try {
      const items = selectedTemplates.map((row) => ({
        name: row.name,
        bot_number: row.bot_number,
      }));
      const res = await templateService.bulkArchiveTemplates(items, action);
      if (res?.status === 200) {
        const succeeded = res?.data?.data?.succeeded?.length ?? 0;
        const failed = res?.data?.data?.failed?.length ?? 0;
        showSnackBar(
          `${action === 'archive' ? 'Archived' : 'Restored'} ${succeeded} template(s)${failed ? `, ${failed} failed` : ''}`,
          failed ? 'warning' : 'success'
        );
        setRowSelection({});
        refreshList();
      } else {
        showSnackBar('Bulk action failed', 'error');
      }
    } catch (error) {
      showSnackBar(error?.message || 'Bulk action failed', 'error');
    }
  };

  const handleClearFilters = () => {
    resetFilters();
    setSearchInput('');
  };

  const openOptinView = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', 'optin');
      next.delete('health_tab');
      next.delete('waba_number');
      next.delete('template_name');
      return next;
    }, { replace: true });
  };

  const handleStatusTabChange = (patch) => {
    if (searchParams.get('view') === 'optin' || searchParams.get('view') === 'health') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('view');
        next.delete('health_tab');
        next.delete('waba_number');
        next.delete('template_name');
        return next;
      }, { replace: true });
    }
    updateFilters(patch);
  };

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      updateFilters({ search: debouncedSearch, page: 0 });
    }
  }, [debouncedSearch, filters.search, updateFilters]);

  useEffect(() => {
    if (sorting?.length > 0) {
      const { id, desc } = sorting[0];
      updateFilters({
        sortField: id || 'created_at',
        sortOrder: desc ? 'desc' : 'asc',
      });
    }
  }, [sorting, updateFilters]);

  const breadcrumbsData = useMemo(() => {
    const base = [
      { label: 'Home', href: '/', icon: <HomeIcon fontSize="small" /> },
      {
        label: 'Templates',
        href: isOptinView ? `${TEMPLATE_BASE_PATH}/template-list` : undefined,
      },
    ];
    if (isOptinView) {
      base.push({ label: 'Opt-in Users' });
    }
    return base;
  }, [isOptinView]);

  const handleChangePage = (newPage) => {
    updateFilters({ page: newPage });
  };

  const handleChangeRowsPerPage = (nextPageSize) => {
    const limit = Number(nextPageSize) || 10;
    updateFilters({ limit, page: 0 });
  };

  const openDetails = (row) => {
    navigate(`${TEMPLATE_BASE_PATH}/template-details/${row.name}/${row.bot_number}`);
  };

  const openPerformance = (row) => {
    navigate(`${TEMPLATE_BASE_PATH}/template-details/${row.name}/${row.bot_number}?tab=performance`);
  };

  const openEdit = (row) => {
    navigate(`${TEMPLATE_BASE_PATH}/edit-template/${row.name}/${row.bot_number}`);
  };

  const requestDelete = (row) => {
    if (!isTemplateDeletable(row)) return;
    setDeleteTarget(row);
  };

  const requestArchive = (row) => {
    setArchiveTarget(row);
  };

  const requestRestore = (row) => {
    setRestoreTarget(row);
  };

  const requestDuplicate = async (row) => {
    try {
      const res = await templateService.getTemplate(row.name, row.bot_number);
      if (res?.status === 200) {
        const cloneFrom = { ...res.data };
        const filterOrg = filters.organisationObj;
        if (
          filterOrg
          && String(filterOrg.organisation_id) === String(cloneFrom.company_id)
          && !getOrganisationNameFromTemplate(cloneFrom)
        ) {
          cloneFrom.organisation_name =
            filterOrg.organisation_name ?? filterOrg.name ?? filterOrg.label ?? '';
        }
        navigate(`${TEMPLATE_BASE_PATH}/create-template`, {
          state: { cloneFrom, sourceName: row.name },
        });
      } else {
        showSnackBar('Failed to load template for duplication', 'error');
      }
    } catch (error) {
      showSnackBar(error?.message || 'Failed to load template for duplication', 'error');
    }
  };

  const handleRowSync = async (row) => {
    const rowKey = `${row.name}::${row.bot_number}`;
    setSyncingRowKey(rowKey);
    try {
      const res = await templateService.syncTemplate({
        botNumber: row.bot_number,
        name: row.name,
        language: row.language,
      });
      const payload = res?.data;
      if (res?.status === 200 && payload?.success !== false) {
        if (payload?.updated > 0) {
          showSnackBar('Template status synced', 'success');
        } else if (payload?.unchanged > 0) {
          showSnackBar('Template status is already up to date', 'info');
        } else {
          showSnackBar('Template could not be synced', 'warning');
        }
        refreshList();
        return;
      }
      const errorText =
        (typeof payload?.error === 'string' && payload.error)
        || payload?.error?.message
        || 'Failed to sync template';
      showSnackBar(errorText, 'error');
    } catch (syncError) {
      showSnackBar(syncError?.message || 'Failed to sync template', 'error');
    } finally {
      setSyncingRowKey(null);
    }
  };

  const handleConfirmArchive = async (confirmed) => {
    const row = archiveTarget;
    setArchiveTarget(null);
    if (!confirmed || !row) return;

    try {
      const res = await templateService.archiveTemplate(row.name, row.bot_number);
      if (res?.status === 200) {
        showSnackBar('Template archived successfully', 'success');
        refreshList();
      } else {
        showSnackBar(res?.data?.data?.general_error || 'Failed to archive template', 'error');
      }
    } catch (error) {
      showSnackBar(error?.message || 'Failed to archive template', 'error');
    }
  };

  const handleConfirmRestore = async (confirmed) => {
    const row = restoreTarget;
    setRestoreTarget(null);
    if (!confirmed || !row) return;

    try {
      const res = await templateService.restoreTemplate(row.name, row.bot_number);
      if (res?.status === 200) {
        showSnackBar('Template restored successfully', 'success');
        refreshList();
      } else {
        showSnackBar(res?.data?.data?.general_error || 'Failed to restore template', 'error');
      }
    } catch (error) {
      showSnackBar(error?.message || 'Failed to restore template', 'error');
    }
  };

  const handleConfirmDelete = async (confirmed) => {
    const row = deleteTarget;
    setDeleteTarget(null);
    if (!confirmed || !row) return;

    try {
      const res = await templateService.deleteTemplate(row.name, row.bot_number);
      if (res?.status === 200) {
        showSnackBar('Template deleted successfully', 'success');
        refreshList();
      } else {
        showSnackBar(res?.message || 'Error deleting template. Please try again.', 'error');
      }
    } catch (error) {
      showSnackBar(error?.message || 'Error deleting template. Please try again.', 'error');
    }
  };

  const renderHeader = (label) => () => <TemplateColumnHeader>{label}</TemplateColumnHeader>;

  const isArchivedView = filters.archived === 'true';

  const columns = useMemo(
    () => {
      const baseColumns = [
      {
        accessorKey: 'index',
        header: 'S.No.',
        Header: renderHeader('S.No.'),
        enableSorting: false,
        size: 52,
        Cell: ({ row }) => filters.page * filters.limit + row.index + 1,
      },
      {
        accessorKey: 'name',
        header: 'Template Name',
        Header: renderHeader('Template Name'),
        size: 280,
        muiTableBodyCellProps: {
          sx: {
            overflow: 'visible',
            whiteSpace: 'normal',
          },
        },
        Cell: ({ row }) => (
          <TemplateNameCell
            row={row.original}
            onOpenDetails={canViewPerformance ? openDetails : undefined}
            onOpenInsights={canViewPerformance ? openPerformance : undefined}
          />
        ),
      },
      {
        accessorKey: 'category',
        header: 'Category',
        Header: renderHeader('Category'),
        // enableSorting: false,
        size: 120,
        Cell: ({ cell }) => (
          <StatusChip value={cell.getValue()} label={getCategoryLabel(cell.getValue())} />
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Header: renderHeader('Status'),
        // enableSorting: false,
        size: 110,
        Cell: ({ row }) => <TemplateStatusCell row={row.original} />,
      },
      {
        accessorKey: 'health',
        header: 'Health',
        Header: renderHeader('Health'),
        enableSorting: false,
        size: 110,
        Cell: ({ row }) => {
          const rowKey = getTemplateRowKey(row.original);
          return (
            <TemplateHealthCell
              row={row.original}
              health={healthMap[rowKey]}
              onCheck={(target) => checkSingleTemplateHealth(target)}
            />
          );
        },
      },
      {
        accessorKey: 'language',
        header: 'Language',
        Header: renderHeader('Language'),
        // enableSorting: false,
        size: 110,
        Cell: ({ cell }) => getLanguageLabel(cell.getValue()),
      },
      {
        accessorKey: 'tags',
        header: 'Tags',
        Header: renderHeader('Tags'),
        // enableSorting: false,
        size: 160,
        Cell: ({ row }) => {
          const tags = row.original?.tags ?? [];
          if (!tags.length) return '—';
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {tags.map((tag) => (
                <Chip key={tag.id} label={tag.name} size="small" variant="outlined" />
              ))}
            </Box>
          );
        },
      },
      {
        accessorKey: 'failed_reason',
        header: 'Rejection Reason',
        Header: renderHeader('Rejection Reason'),
        // enableSorting: false,
        size: 200,
        Cell: ({ row }) => {
          if (!isRejectedTemplate(row.original)) return '—';
          const reason = getRejectionReason(row.original);
          return reason ? <EllipsisWithTooltip text={reason} maxWidth={190} /> : '—';
        },
      },
      ...(isArchivedView
        ? [
            {
              accessorKey: 'archived_at',
              header: 'Archived On',
              Header: renderHeader('Archived On'),
              enableSorting: false,
              size: 160,
              Cell: ({ row }) =>
                row.original.archived_at
                  ? dateFormate(new Date(row.original.archived_at), 'DD-MM-YYYY hh:mm A')
                  : '—',
            },
            {
              accessorKey: 'delete_eligibility',
              header: 'Delete Eligibility',
              Header: renderHeader('Delete Eligibility'),
              enableSorting: false,
              size: 140,
              Cell: ({ row }) => {
                const label = getArchiveDeleteLabel(
                  row.original,
                  row.original.archive_delete_wait_days
                );
                if (!label) return '—';
                const isReady = label === 'Ready to delete';
                return (
                  <StatusChip
                    value={isReady ? 'APPROVED' : 'PENDING'}
                    label={label}
                  />
                );
              },
            },
          ]
        : []),
      {
        accessorKey: 'updated_at',
        header: 'Updated At',
        Header: renderHeader('Updated At'),
        size: 160,
        Cell: ({ row }) =>
          row.original.updated_at
            ? dateFormate(new Date(row.original.updated_at), 'DD-MM-YYYY hh:mm A')
            : '—',
      },
      {
        accessorKey: 'actions',
        header: 'Action',
        Header: renderHeader('Action'),
        enableSorting: false,
        size: 72,
        minSize: 72,
        maxSize: 72,
        grow: false,
        muiTableHeadCellProps: {
          sx: styles.actionColumnHeadCell,
        },
        muiTableBodyCellProps: {
          sx: styles.actionColumnBodyCell,
        },
        Cell: ({ row }) => (
          <TemplateRowActions
            row={row.original}
            onEdit={openEdit}
            onSend={openSendDialog}
            onInsights={canViewPerformance ? openPerformance : undefined}
            onDelete={requestDelete}
            onArchive={requestArchive}
            onRestore={requestRestore}
            onDuplicate={requestDuplicate}
            onSync={handleRowSync}
            onCheckHealth={(targetRow) =>
              checkSingleTemplateHealth(targetRow)
            }
            isSyncing={syncingRowKey === `${row.original.name}::${row.original.bot_number}`}
          />
        ),
      },
    ];

      return baseColumns;
    },
    [
      filters.limit,
      filters.page,
      filters.archived,
      isArchivedView,
      styles.actionColumnHeadCell,
      styles.actionColumnBodyCell,
      openDetails,
      openPerformance,
      canViewPerformance,
      syncingRowKey,
      healthMap,
      checkSingleTemplateHealth,
    ]
  );

  return (
    <Box sx={{ ...styles.pageRoot, minHeight: 0 }}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(_event, item) => {
          if (item?.href) navigate(item.href);
        }}
      />

      <TemplatePageHeader
        showCreate={canCreate}
        onCreate={() => navigate(`${TEMPLATE_BASE_PATH}/create-template`)}
        onOpenHealth={isHealthView ? closeHealthView : () => openHealthView(0, filters.botNumber)}
        isHealthView={isHealthView}
        showCheckPageHealth={templates.length > 0}
        isCheckingPageHealth={isCheckingPageHealth}
        pageHealthProgress={pageHealthProgress}
        onCheckPageHealth={() => checkBatchTemplatesHealth(templates)}
      />

      <Box sx={styles.dashboardSurface}>
        <TemplateStatusTabs
          status={filters.status}
          archived={filters.archived ?? 'false'}
          counts={statusTabCounts}
          isLoading={countsLoading}
          showOptin={canViewOptin}
          isOptinView={isOptinView || isHealthView}
          onTabChange={handleStatusTabChange}
          onOptinSelect={openOptinView}
        />

        {isHealthView ? (
          <HealthPanel
            onBack={closeHealthView}
            initialTab={healthTab}
            initialWabaNumber={healthWabaNumber || filters.botNumber || ''}
            initialTemplateName={healthTemplateName}
            companyId={activeCompanyId}
            templates={templates}
            selectedBotNumber={filters.botNumber}
          />
        ) : isOptinView ? (
          <OptinUsersPanel onTotalCountChange={setOptinCount} />
        ) : (
        <>
          <HealthLiveBar
            currentBotNumber={filters.botNumber}
            companyId={activeCompanyId}
            onOpenFullHealth={() => openHealthView(0, filters.botNumber)}
          />

          <TemplateFiltersBar
            filters={filters}
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            onFilterChange={updateFilters}
            onClear={handleClearFilters}
          />

          {canEdit && selectedTemplates.length > 0 ? (
            <Box sx={{ ...styles.bulkSelectionBar, mx: { xs: 1, sm: 1.5 }, mt: 0, mb: 1 }}>
              <Typography variant="body2" sx={{ alignSelf: 'center', fontWeight: 600 }}>
                {selectedTemplates.length} selected
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<MonitorHeartIcon sx={{ fontSize: 16 }} />}
                onClick={() => checkBatchTemplatesHealth(selectedTemplates)}
                disabled={isCheckingPageHealth}
                sx={styles.bulkActionButton}
              >
                {isCheckingPageHealth
                  ? `Checking (${pageHealthProgress.current}/${pageHealthProgress.total})...`
                  : 'Check Health'}
              </Button>
              {!isArchivedView ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setBulkAction('archive')}
                  sx={styles.bulkActionButton}
                >
                  Archive
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setBulkAction('restore')}
                  sx={styles.bulkActionButton}
                >
                  Restore
                </Button>
              )}
            </Box>
          ) : null}

          <TemplateDataTable
            columns={columns}
            data={templates}
            isLoading={isLoading}
            error={error}
            enableRowSelection={canEdit}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            onSortingChange={setSorting}
            getRowId={(row) => `${row.name}::${row.bot_number}`}
            emptyStateMessage="No templates found."
            sorting={sorting}
          />

          {count > 0 ? (
            <TablePagination
              count={count}
              page={filters.page}
              rowsPerPage={filters.limit}
              onPageChange={(_event, newPage) => handleChangePage(newPage)}
              onRowsPerPageChange={(event) => handleChangeRowsPerPage(event.target.value)}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          ) : null}
        </>
        )}
      </Box>

      <TemplateSendDialog />

      <ConfirmBox
        open={Boolean(deleteTarget)}
        onClose={handleConfirmDelete}
        options={{
          title: `Delete ${deleteTarget?.name ?? 'Template'}`,
          text: 'This will permanently delete the template from Meta and cannot be undone.',
          confirmButtonText: 'Yes',
          cancelButtonText: 'Cancel',
          confirmButtonColor: 'error',
        }}
      />

      <ConfirmBox
        open={Boolean(archiveTarget)}
        onClose={handleConfirmArchive}
        options={{
          title: `Archive ${archiveTarget?.name ?? 'Template'}`,
          text: 'This template will be hidden from active list and cannot be sent or edited until restored.',
          confirmButtonText: 'Archive',
          cancelButtonText: 'Cancel',
        }}
      />

      <ConfirmBox
        open={Boolean(restoreTarget)}
        onClose={handleConfirmRestore}
        options={{
          title: `Restore ${restoreTarget?.name ?? 'Template'}`,
          text: 'This template will return to the active list and can be sent or edited again. The delete eligibility timer will reset if archived again later.',
          confirmButtonText: 'Restore',
          cancelButtonText: 'Cancel',
        }}
      />
      <ConfirmBox
        open={Boolean(bulkAction)}
        onClose={handleBulkArchiveRestore}
        options={{
          title: bulkAction === 'archive' ? 'Archive selected templates' : 'Restore selected templates',
          text:
            bulkAction === 'archive'
              ? `${selectedTemplates.length} template(s) will be hidden from the active list.`
              : `${selectedTemplates.length} template(s) will return to the active list.`,
          confirmButtonText: bulkAction === 'archive' ? 'Archive' : 'Restore',
          cancelButtonText: 'Cancel',
        }}
      />
    </Box>
  );
}
